import React, { useCallback, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { useTranslation } from 'react-i18next';
import { usePermission } from 'providers/RightsHooks';
import { UPDATE_BEHAVIOR_PARAM, UPDATE_INITIALIZATION_PARAM } from '../mutations';
import { isJsonString, getPlainStringFromJson } from 'utils/functions';
import EditJsonContentDialog from 'components/common/EditJsonContentDialog';
import ParameterItem from './ParameterItem';
import { useMutation, useSubscription } from 'hooks/apollo';
import { ADMIN_INITIALIZATION_PARAM_WRITE } from 'constants/rightsCodes';
import { useToast } from 'hooks/toasts/useToast';
import { useApolloClient } from '@apollo/client';
import { BEHAVIOR_PARAMETER_CHANGED, INITIALIZATION_PARAMETER_CHANGED, PARAMETER_VALIDATION_FAILED } from '../subscriptions';
import { PARAMETER_LIST_QUERY } from '../queries';
import { addCachedDetail } from 'utils/apollo/cacheExtensionFunctions';
import { useReactiveVar } from '@apollo/client';
import variables from '../variables';
import { statusCodes } from './StatusIndicator';

const ParameterItemContainer = ({ data }) => {
  const isJson = data.editorType === 'Json';
  const client = useApolloClient();
  const [t] = useTranslation();
  const addToast = useToast();
  const canWrite = usePermission(ADMIN_INITIALIZATION_PARAM_WRITE);

  const [parameterLens, , resetParameter] = useChangeTrackingLens(data);
  const parameterItem = parameterLens |> get;

  const [indicators, setIndicators] = useState({
    status: null,
    editing: false,
    pending: false
  });

  const { status, editing, pending } = indicators;

  const [jsonValue, setJsonValue] = useState(null);
  const [showJsonDialog, setShowJsonDialog] = useState(false);
  const canEditParam = parameterItem.isInitializationParam ? (canWrite ? true : false) : true;
  const parametersHealthChecks = useReactiveVar(variables.parametersHealthChecks);

  const [updateBehaviorParam] = useMutation(UPDATE_BEHAVIOR_PARAM, {
    onError: () => {
      setIndicators(prev => ({ ...prev, pending: false }));
    }
  });

  const [updateInitializationParam] = useMutation(UPDATE_INITIALIZATION_PARAM, {
    onError: () => {
      setIndicators(prev => ({ ...prev, pending: false }));
    }
  });

  useEffect(() => {
    resetParameter(data);
  }, [data, resetParameter]);

  const handleSave = useCallback(() => {
    const input = Object.assign({}, parameterItem);
    if (isJson) {
      input.defaultValue = getPlainStringFromJson(jsonValue);
    }
    if (input.isInitializationParam) {
      updateInitializationParam({
        variables: {
          input: { paramCode: input.paramCode, defaultValue: input.defaultValue, changedByUserAction: true }
        }
      });
    } else {
      updateBehaviorParam({
        variables: {
          input: { paramCode: input.paramCode, defaultValue: input.defaultValue, changedByUserAction: true }
        }
      });
    }
  }, [isJson, parameterItem, jsonValue, updateBehaviorParam, updateInitializationParam]);

  const handleEditMode = useCallback(() => {
    if (isJson) {
      if (isJsonString(data.defaultValue, t('JsonFileContent.WrongContent'))) {
        setJsonValue(JSON.parse(data.defaultValue));
        setShowJsonDialog(true);
      }
    } else {
      setIndicators(prev => ({ ...prev, editing: true }));
    }
  }, [isJson, data.defaultValue, t]);

  const handleChangeJsonValue = input => {
    setJsonValue(input.updated_src);
  };

  const handleCancel = useCallback(() => {
    resetParameter(data);
    setShowJsonDialog(false);
    setIndicators(prev => ({ ...prev, editing: false, status: null }));
  }, [resetParameter, data]);

  const handleDelete = useCallback(() => {
    const input = Object.assign({}, parameterItem);
    if (input.isInitializationParam) {
      updateInitializationParam({
        variables: {
          input: {
            paramCode: input.paramCode,
            defaultValue: null,
            changedByUserAction: true
          }
        }
      });
    } else {
      updateBehaviorParam({
        variables: {
          input: {
            paramCode: input.paramCode,
            defaultValue: null,
            changedByUserAction: true
          }
        }
      });
    }
  }, [parameterItem, updateBehaviorParam, updateInitializationParam]);

  const finishAction = useCallback(
    parameter => {
      const parametersHealthChecksRun = parametersHealthChecks && parametersHealthChecks.length > 0;
      addToast(t('BehaviorParamTypes.SavingSucceeded'), 'success');
      const status = parametersHealthChecksRun ? { code: statusCodes.OK } : null;
      if (parametersHealthChecksRun)
        updateParametersHealthChecks(parametersHealthChecks, parameter.paramCode, parameter.isInitializationParam, statusCodes.OK, null);
      setIndicators(prev => ({ ...prev, pending: false, editing: false, status }));
    },
    [addToast, parametersHealthChecks, t]
  );

  useSubscription(BEHAVIOR_PARAMETER_CHANGED, {
    variables: { code: data.paramCode },
    onData: ({
      data: {
        data: { behaviorParameterChanged }
      }
    }) => {
      setShowJsonDialog(false);
      const parameter = behaviorParameterChanged.parameter;
      const paramCode = data.paramCode;
      addCachedDetail(
        client,
        { name: 'initializationParameters', value: PARAMETER_LIST_QUERY },
        { paramCode },
        x => x.paramCode === paramCode,
        parameter,
        ['paramCode']
      );
      finishAction(parameter);
    }
  });

  useSubscription(INITIALIZATION_PARAMETER_CHANGED, {
    variables: { code: data.paramCode },
    onData: ({
      data: {
        data: { initializationParameterChanged }
      }
    }) => {
      setShowJsonDialog(false);
      const parameter = initializationParameterChanged.parameter;
      const paramCode = data.paramCode;
      addCachedDetail(
        client,
        { name: 'initializationParameters', value: PARAMETER_LIST_QUERY },
        { paramCode },
        x => x.paramCode === paramCode,
        parameter,
        ['paramCode']
      );
      finishAction(parameter);
    }
  });

  useSubscription(PARAMETER_VALIDATION_FAILED, {
    variables: { code: data.paramCode },
    onData: ({
      data: {
        data: { parameterValidationFailed }
      }
    }) => {
      const { status, errorMessage } = parameterValidationFailed;
      setIndicators(prev => ({ ...prev, status: { code: status, errorMessage } }));
      const toastType = status === 'ERROR' ? 'error' : 'warning';
      addToast(errorMessage, toastType);
    }
  });

  const parameterStatus = useMemo(() => {
    if (!parametersHealthChecks?.length) return null;
    const phc = parametersHealthChecks.find(
      param => param.parameterCode === data.paramCode && param.isInitializationParam === (data.isInitializationParam ?? false)
    );
    if (!phc) return null;
    return { code: phc.status, errorMessage: phc.errorMessage };
  }, [data.isInitializationParam, data.paramCode, parametersHealthChecks]);

  useEffect(() => {
    setIndicators(prev => ({ ...prev, status: parameterStatus }));
  }, [parameterStatus]);

  return (
    <>
      <ParameterItem
        parameterItem={parameterItem}
        parameterLens={parameterLens}
        status={status}
        editing={editing}
        pending={pending}
        disabled={!canEditParam}
        onSave={handleSave}
        onEdit={handleEditMode}
        onCancel={handleCancel}
        onDelete={handleDelete}
      />
      {showJsonDialog && (
        <EditJsonContentDialog
          content={jsonValue}
          open={true}
          onChange={handleChangeJsonValue}
          onClose={handleCancel}
          onCancel={handleCancel}
          onConfirm={handleSave}
        />
      )}
    </>
  );
};

const updateParametersHealthChecks = (parametersHealthChecks, paramCode, isInitializationParam, statusCode, message) => {
  if (!parametersHealthChecks?.length) return;
  const newArray = [...parametersHealthChecks];
  const target = newArray.find(
    param => param.parameterCode === paramCode && param.isInitializationParam === (isInitializationParam ?? false)
  );
  if (!target) return;
  target.status = statusCode;
  target.errorMessage = message;
  variables.parametersHealthChecks(newArray);
};

ParameterItemContainer.propTypes = {
  data: PropTypes.object.isRequired
};

export default ParameterItemContainer;
